import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box, Button, Flex, FormControl, FormLabel, Input, Text, useToast, Spinner,
  InputGroup, InputRightElement, IconButton,
} from "@chakra-ui/react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useAuthentication } from "../../wrappers/UseAuthentication";
import { useSupplierRetailer } from "../../wrappers/SupplierRetailerContext";
import { usePageAccess } from "../../wrappers/PageAccessContext";

const API = process.env.REACT_APP_API_URL;

/**
 * Public invite-registration page (/register?token=...).
 *
 * The org admin invited this email address; the invitee picks their own name
 * and password here, then is signed straight in. Token validity is judged by
 * the backend — used/expired/invalid links all render a plain explanation.
 */
const Register = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const toast = useToast();
  const { signIn } = useAuthentication();
  const { fetchUserAccess } = useSupplierRetailer();
  const { fetchPageAccess } = usePageAccess();

  const [checking, setChecking] = useState(true);
  const [invite, setInvite] = useState(null); // { useremail, org_name }
  const [problem, setProblem] = useState(null); // message when the link is unusable
  const [fname, setFname] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false); // one toggle covers both fields
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setProblem("This invitation link is not valid.");
      setChecking(false);
      return;
    }
    axios
      .post(`${API}/login/invite-details`, { token })
      .then((res) => {
        if (res.data?.status === 1) setInvite(res.data.data);
        else setProblem(res.data?.message || "This invitation link is not valid.");
      })
      .catch(() => setProblem("Could not check this invitation — please try again."))
      .finally(() => setChecking(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast({ title: "Passwords do not match", status: "warning", duration: 4000, isClosable: true });
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/login/accept-invite`, { token, fname, password });
      if (res.data?.status !== 1) {
        toast({ title: "Registration failed", description: res.data?.message, status: "error", duration: 8000, isClosable: true });
        return;
      }

      // Mirror Signin's handleAuthSuccess: persist identity + bearer token,
      // hydrate the contexts, then enter the app.
      const data = res.data.data;
      const userid = data[0].userid;
      const user_type = data[0].user_type || "default";
      localStorage.setItem("userid", userid);
      localStorage.setItem("fname", data[0].fname);
      localStorage.setItem("user_type", user_type);
      localStorage.setItem("username", data[0].username);
      localStorage.setItem("staySignedIn", "false");
      localStorage.removeItem("refreshToken");
      if (res.data.accessToken) localStorage.setItem("accessToken", res.data.accessToken);

      await fetchUserAccess(userid);
      await fetchPageAccess(userid);
      signIn(user_type);
      toast({ title: "Welcome to SpearBI", description: "Your account is ready.", status: "success", duration: 3000, isClosable: true });
      setTimeout(() => navigate("/sales"), 600);
    } catch (error) {
      toast({ title: "Registration failed", description: error?.response?.data?.message, status: "error", duration: 8000, isClosable: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Flex minH="100vh" align="center" justify="center" bg="gray.50" px={4}>
      <Box w="100%" maxW="420px" bg="white" p={8} borderRadius="lg" boxShadow="md">
        <Text fontSize="xl" fontWeight="700" mb={1}>
          Create your SpearBI account
        </Text>

        {checking && (
          <Flex justify="center" py={10}>
            <Spinner />
          </Flex>
        )}

        {!checking && problem && (
          <Box mt={4}>
            <Text fontSize="sm" color="gray.600">{problem}</Text>
            <Button mt={6} w="100%" onClick={() => navigate("/signin")}>
              Go to sign in
            </Button>
          </Box>
        )}

        {!checking && invite && (
          <>
            <Text fontSize="sm" color="gray.500" mb={6}>
              {invite.org_name ? `Invited by ${invite.org_name}. ` : ""}You will sign in as{" "}
              <Text as="span" fontWeight="600" color="gray.700">{invite.useremail}</Text>
            </Text>
            <form onSubmit={handleSubmit}>
              <FormControl isRequired mb={3}>
                <FormLabel fontSize="sm">Your full name</FormLabel>
                <Input value={fname} onChange={(e) => setFname(e.target.value)} placeholder="ie: Jane Smith" />
              </FormControl>
              <FormControl isRequired mb={3}>
                <FormLabel fontSize="sm">Choose a password</FormLabel>
                <InputGroup>
                  <Input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} />
                  <InputRightElement>
                    <IconButton
                      size="sm"
                      variant="ghost"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      icon={showPassword ? <FaEyeSlash /> : <FaEye />}
                      onClick={() => setShowPassword((s) => !s)}
                      tabIndex={-1}
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>
              <FormControl isRequired mb={6}>
                <FormLabel fontSize="sm">Confirm password</FormLabel>
                <InputGroup>
                  <Input type={showPassword ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
                  <InputRightElement>
                    <IconButton
                      size="sm"
                      variant="ghost"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      icon={showPassword ? <FaEyeSlash /> : <FaEye />}
                      onClick={() => setShowPassword((s) => !s)}
                      tabIndex={-1}
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>
              <Button type="submit" w="100%" isLoading={submitting}>
                Create account & sign in
              </Button>
            </form>
          </>
        )}
      </Box>
    </Flex>
  );
};

export default Register;
